import type {
  Comment,
  FeedCursor,
  FeedPage,
  GeoServer,
  Post,
  PostMention,
  PublicUser,
  ReportReason,
} from '@sirocco/shared';
import { POST_MAX_LENGTH } from '@sirocco/shared';
import { requireUid } from './auth';
import { appeler } from './callable';
import { uploadIfDataUrl } from './media';
import { normalizeUsername, paths } from './paths';
import { keysOf, loadUser, loadUsers, toIso, unknownUser } from './records';
import { keepCited, nomsPossibles } from '../utils/mentions';
import {
  SiroccoDataError,
  bumpCounter,
  exists,
  newKey,
  readKeys,
  readList,
  readOptionalKeys,
  readOptionalList,
  readOptionalValue,
  readValue,
  removeValue,
  updateValues,
  writeValue,
} from './rtdb';
import { t } from '../i18n';

/**
 * Feed, commentaires, likes et signets.
 *
 * La base ne connait que des faits bruts : un post, la liste de ceux qui
 * l'ont like, la liste de mes signets. Les champs `liked` et `bookmarked`
 * du type `Post` dependent, eux, du membre qui regarde : ils sont
 * recomposes a chaque lecture, jamais stockes.
 */

/** Un post, tel qu'il est ecrit dans `posts/{serverId}/{postId}`. */
/**
 * Un contenu masque par la moderation.
 *
 * Le drapeau plutot que la suppression : effacer emporterait la preuve de
 * ce qui a ete signale, et un moderateur qui se trompe n'aurait plus rien
 * a retablir. Il n'est jamais ecrit par un client — seule la Cloud
 * Function de moderation y touche.
 */
interface Masquable {
  masque?: boolean;
}

interface PostRecord extends Masquable {
  authorId: string;
  content: string;
  createdAt: number;
  likeCount: number;
  commentCount: number;
  /** Absent sur tout ce qui a ete range avant que le compteur existe. */
  bookmarkCount?: number;
  attachedEventId: string | null;
  /** Photo jointe, en data URL. Voir `utils/image`. */
  imageUrl?: string | null;
  mentions?: PostMention[];
  /** Lieu precis dans la ville, saisi librement. */
  location?: string | null;
  /** Le fil d'association ou il a ete publie, pose par la fonction qui publie. */
  asso?: string | null;
}

interface CommentRecord extends Masquable {
  authorId: string;
  content: string;
  createdAt: number;
  /* Absent sur les reponses d'avant l'arbre, et sur celles posees
     directement sous le post. */
  parentId?: string;
  /* Absent sur les reponses d'avant les mentions. */
  mentions?: PostMention[];
  /* Absent sur tout ce qui a ete range avant que le compteur existe. */
  bookmarkCount?: number;
}

/** Un signet porte son serveur : sans lui on ne saurait pas ou relire le post. */
interface BookmarkRecord {
  serverId: string;
  createdAt: number;
}

/** Le serveur n'est pas dans le post : il vient du chemin, on le rattache ici. */
type PostRow = PostRecord & { id: string; serverId: string };

/* --- Lecture ------------------------------------------------------- */

/**
 * Combien de posts par page.
 *
 * Le fil se lisait d'un bloc, sans limite : ouvrir l'ecran d'accueil
 * retelechargeait tout ce que le spot a jamais publie. Une page suffit a
 * remplir plusieurs ecrans ; la suite arrive quand on approche du bas.
 */
const PAGE = 20;

/**
 * Une page du fil, de la plus recente vers la plus ancienne.
 *
 * Les posts masques et ceux des membres bloques sont ecartes apres la
 * lecture : une page peut donc en afficher moins que `PAGE` sans que le
 * fil soit fini. C'est le curseur, et lui seul, qui dit s'il reste
 * quelque chose avant.
 */
export async function listFeed(
  serverId: string,
  before?: FeedCursor,
  search?: string,
  /** Combien de posts lire d'un coup : une page, ou la fenetre du classement « Pour toi ». */
  taille = PAGE,
): Promise<FeedPage> {
  const uid = requireUid();
  const needle = fold(search ?? '');

  /*
    Une recherche lit le fil entier plutot qu'une page.

    La Realtime Database ne sait pas chercher dans du texte : le tri se
    fait ici, en memoire. Paginer une recherche n'aurait donc aucun sens
    — la deuxieme page pourrait ne rien contenir alors que la troisieme
    deborde. Le jour ou un spot comptera des milliers de posts, il faudra
    un index de recherche ; a l'echelle d'une ville qui demarre, lire la
    liste coute moins qu'un aller-retour de plus.
  */
  const [records, hidden, blocked, comptesMasques, bookmarks] = await Promise.all([
    readList<PostRecord>(paths.posts(serverId), {
      orderBy: 'createdAt',
      limit: needle ? undefined : taille,
      before: before ? { value: before.createdAt, key: before.id } : undefined,
    }),
    readKeys(paths.hiddenPosts(uid)),
    readOptionalKeys(paths.blockedUsers(uid)),
    readOptionalKeys(paths.hiddenUsers(uid)),
    readBookmarks(uid),
  ]);

  const masked = new Set(hidden);
  /* Bloque ou masque : dans les deux cas, ses posts quittent mon fil. */
  const muted = new Set([...blocked, ...comptesMasques]);
  const rows = records
    .filter(
      (record) =>
        !record.masque &&
        !masked.has(record.id) &&
        !muted.has(record.authorId) &&
        matches(record, needle),
    )
    .map((record) => ({ ...record, serverId }));

  /*
    Le curseur se prend sur la lecture brute, pas sur les posts affiches :
    une page entierement masquee laisserait sinon croire que le fil est
    fini. Moins de `PAGE` enregistrements, en revanche, veut bien dire
    qu'il n'y a plus rien avant. Une recherche, elle, a tout lu d'un coup.
  */
  const plusAncien = records[0];
  const cursor =
    needle || records.length < taille || !plusAncien
      ? null
      : { createdAt: plusAncien.createdAt, id: plusAncien.id };

  return { posts: await hydrate(uid, sortByRecent(rows), new Set(keysOf(bookmarks))), cursor };
}

/**
 * Le fil d'une association : ses posts, du plus recent au plus ancien.
 *
 * Une requete sur le champ `asso`, que les regles indexent : la base rend
 * les seuls posts de cette association, sans lire tout le fil de l'ecole.
 */
export async function listerLesPostsDeLAsso(serverId: string, asso: string): Promise<Post[]> {
  const uid = requireUid();
  const [records, hidden, blocked, comptesMasques, bookmarks] = await Promise.all([
    readList<PostRecord>(paths.posts(serverId), { orderBy: 'asso', egalA: asso }),
    readKeys(paths.hiddenPosts(uid)),
    readOptionalKeys(paths.blockedUsers(uid)),
    readOptionalKeys(paths.hiddenUsers(uid)),
    readBookmarks(uid),
  ]);
  const masked = new Set(hidden);
  const muted = new Set([...blocked, ...comptesMasques]);
  const rows = records
    .filter((record) => !record.masque && !masked.has(record.id) && !muted.has(record.authorId))
    .map((record) => ({ ...record, serverId }));
  return hydrate(uid, sortByRecent(rows), new Set(keysOf(bookmarks)));
}

export async function listBookmarked(): Promise<Post[]> {
  const uid = requireUid();
  const bookmarks = await readBookmarks(uid);

  // Du plus recemment mis en signet : c'est l'ordre de l'ecran "Signets",
  // pas celui de publication.
  const marks = Object.entries(bookmarks).sort(
    ([, left], [, right]) => (right.createdAt ?? 0) - (left.createdAt ?? 0),
  );
  const records = await Promise.all(
    marks.map(([postId, mark]) => readValue<PostRecord>(paths.post(mark.serverId, postId))),
  );

  // Un post supprime par son auteur laisse un signet orphelin chez les
  // autres membres : on le saute plutot que d'afficher un trou.
  const rows = marks.flatMap<PostRow>(([postId, mark], index) => {
    const record = records[index];
    return record ? [{ ...record, id: postId, serverId: mark.serverId }] : [];
  });
  return hydrate(uid, rows, new Set(Object.keys(bookmarks)));
}

/**
 * Posts d'un membre, toutes destinations confondues.
 * La base n'a pas d'index par auteur : on balaie les serveurs, qui se
 * comptent en dizaines, et les feeds sont lus en parallele. Le jour ou
 * le catalogue grossira, il faudra tenir un `postsByAuthor/{uid}`.
 */
export async function listByAuthor(authorId: string): Promise<Post[]> {
  const uid = requireUid();
  const [servers, bookmarks] = await Promise.all([
    readList<GeoServer>(paths.servers()),
    readBookmarks(uid),
  ]);

  const feeds = await Promise.all(
    /* Un serveur prive dont on n'a pas la cle ne rend rien, plutot que
       de faire tomber la page entiere. */
    servers.map((server) => readOptionalList<PostRecord>(paths.posts(server.id))),
  );
  const rows = feeds.flatMap<PostRow>((feed, index) =>
    feed
      .filter((record) => record.authorId === authorId)
      .map((record) => ({ ...record, serverId: servers[index].id })),
  );
  return hydrate(uid, sortByRecent(rows), new Set(keysOf(bookmarks)));
}

/**
 * Un post a partir de son seul identifiant.
 *
 * Le fil de commentaires recevait jusqu'ici le post tout fait, passe dans
 * l'etat de navigation par l'ecran d'ou l'on venait. Arriver par un autre
 * chemin — la cloche du fil, un lien colle — ouvrait donc les reponses
 * sans ce a quoi elles repondent.
 *
 * `postIndex` evite d'avoir a balayer les spots : il dit dans quelle
 * ville le post vit, et deux lectures suffisent.
 */
export async function readPost(postId: string): Promise<Post | null> {
  const uid = requireUid();

  const index = await readOptionalValue<{ serverId?: string }>(paths.postIndex(postId));
  if (!index?.serverId) return null;

  const [record, bookmarks] = await Promise.all([
    readValue<PostRecord>(paths.post(index.serverId, postId)),
    readBookmarks(uid),
  ]);
  // Supprime depuis : la ligne d'activite survit a ce qu'elle raconte.
  if (!record) return null;

  const [post] = await hydrate(
    uid,
    [{ ...record, id: postId, serverId: index.serverId }],
    new Set(keysOf(bookmarks)),
  );
  return post ?? null;
}

export async function listComments(postId: string): Promise<Comment[]> {
  const uid = requireUid();
  // Un commentaire masque par la moderation quitte le fil, sans laisser
  // de trou : il n'est ni compte ni affiche.
  /*
    Ni les reponses de ceux que j'ai bloques, ni celles de ceux que j'ai
    masques : on ne voit plus rien de ce qu'ils ecrivent, sous leurs posts
    comme sous ceux des autres.
  */
  const [lues, bloques, masques] = await Promise.all([
    readList<CommentRecord>(paths.comments(postId)),
    readOptionalKeys(paths.blockedUsers(uid)),
    readOptionalKeys(paths.hiddenUsers(uid)),
  ]);
  const ecartes = new Set([...bloques, ...masques]);
  const records = lues.filter((record) => !record.masque && !ecartes.has(record.authorId));
  if (records.length === 0) return [];

  /*
    Les likes se comptent en lisant leurs cles, sans compteur a tenir.

    Un compteur vivrait dans le commentaire, et les regles en reservent
    l'ecriture a son auteur : personne d'autre ne pourrait l'incrementer.
    Compter les cles coute une lecture par commentaire, menees de front —
    a l'echelle d'un fil de reponses, c'est sans commune mesure avec la
    complexite d'un compteur qu'il faudrait reparer.
  */
  const [authors, likers, bookmarks] = await Promise.all([
    loadUsers(records.map((record) => record.authorId)),
    Promise.all(records.map((record) => readOptionalKeys(paths.commentLikes(record.id)))),
    readOptionalValue<Record<string, true>>(paths.bookmarkedComments(uid)),
  ]);

  /*
    Le nombre de reponses de chacun : ses reponses directes.

    Compte ici plutot que stocke : un compteur dans le commentaire ne
    pourrait etre incremente que par son auteur, les regles y reservant
    l'ecriture — et il faudrait le reparer a chaque suppression. Le fil
    est deja entierement en memoire a ce stade, donc le compte ne coute
    rien de plus.

    Les directes seulement, comme sous le post, et a chaque etage : on
    comptait aussi les reponses des reponses, et un commentaire a une
    seule reponse — elle-meme reprise une fois — affichait 2. Chaque
    etage dit ce qu'on trouve en l'ouvrant.
  */
  const reponses = new Map<string, number>();
  for (const record of records) {
    const parent = record.parentId;
    if (parent) reponses.set(parent, (reponses.get(parent) ?? 0) + 1);
  }

  return (
    records
      .map((record, index) => ({
        id: record.id,
        postId,
        author: authors.get(record.authorId)!,
        parentId: record.parentId ?? null,
        content: record.content,
        mentions: record.mentions ?? [],
        replyCount: reponses.get(record.id) ?? 0,
        createdAt: toIso(record.createdAt),
        likeCount: likers[index].length,
        bookmarkCount: record.bookmarkCount ?? 0,
        liked: likers[index].includes(uid),
        bookmarked: Boolean(bookmarks?.[record.id]),
        at: record.createdAt,
      }))
      // Un fil de discussion se lit du plus ancien au plus recent.
      .sort((left, right) => left.at - right.at)
      .map(({ at: _at, ...comment }) => comment)
  );
}

/**
 * Like d'un commentaire : la meme bascule que sur un post.
 *
 * **L'etat vient de la base, jamais de ce qu'on nous passe.** La version
 * precedente lisait `comment.liked` : un ecran qui peignait d'abord et
 * envoyait ensuite l'objet deja retourne faisait alors deux bascules
 * pour un appui — on ecrivait le retrait d'un like qui n'existait pas,
 * et le coeur revenait a vide sous le doigt. C'est ce qui cassait le
 * like de la reponse ouverte. Le post, lui, n'a jamais eu ce defaut :
 * `toggleLike` relit la base, et c'est pour cette raison. Un appelant ne
 * peut plus se tromper.
 *
 * Le nombre se recompte au lieu de s'incrementer : un commentaire n'a
 * pas de compteur stocke — c'est la branche des likes qui fait foi — et
 * la relire coute une lecture pour un chiffre juste.
 */
export async function toggleCommentLike(comment: Comment): Promise<Comment> {
  const uid = requireUid();
  const likePath = paths.commentLike(comment.id, uid);
  const aime = await exists(likePath);

  if (aime) {
    await removeValue(likePath);
  } else {
    await writeValue(likePath, true);
  }

  const total = (await readOptionalKeys(paths.commentLikes(comment.id))).length;
  return { ...comment, liked: !aime, likeCount: total };
}

/**
 * Signet sur un commentaire.
 *
 * On range le post d'origine avec : sans lui, retrouver le commentaire
 * demanderait de parcourir tous les posts du spot pour savoir sous lequel
 * il vit.
 */
export async function toggleCommentBookmark(comment: Comment): Promise<Comment> {
  const uid = requireUid();
  const markPath = paths.bookmarkedComment(uid, comment.id);
  // Meme regle que le like : c'est la base qui dit ou l'on en est.
  const range = await exists(markPath);

  /*
    Le compteur est pose sur la reponse elle-meme, la ou celui des likes
    se compte a la volee dans sa branche.

    Ce n'est pas une incoherence : les likes d'une reponse se lisent deja
    une par une pour savoir si j'ai aime, la longueur vient donc pour
    rien. Un signet, lui, n'a pas besoin qu'on sache qui a range — juste
    combien — et un champ evite une lecture par reponse a chaque
    ouverture d'un fil.
  */
  const [, bookmarkCount] = await Promise.all([
    range
      ? removeValue(markPath)
      : writeValue(markPath, { postId: comment.postId, createdAt: Date.now() }),
    bumpCounter(compteurDeReponse(comment.postId, comment.id), range ? -1 : 1),
  ]);

  return { ...comment, bookmarked: !range, bookmarkCount: Math.max(0, bookmarkCount) };
}

/**
 * Mes commentaires mis en signet, du plus recemment range.
 *
 * Un commentaire efface — ou dont le post a disparu — laisse un signet
 * orphelin : on le saute plutot que d'afficher un trou.
 */
export async function listBookmarkedComments(): Promise<Comment[]> {
  const uid = requireUid();
  const marks = await readOptionalValue<Record<string, { postId: string; createdAt: number }>>(
    paths.bookmarkedComments(uid),
  );
  if (!marks) return [];

  const wanted = Object.entries(marks).sort(
    ([, left], [, right]) => right.createdAt - left.createdAt,
  );

  const threads = new Map<string, Comment[]>();
  await Promise.all(
    [...new Set(wanted.map(([, mark]) => mark.postId))].map(async (postId) => {
      threads.set(postId, await listComments(postId));
    }),
  );

  return wanted.flatMap(([commentId, mark]) => {
    const found = threads.get(mark.postId)?.find((comment) => comment.id === commentId);
    return found ? [found] : [];
  });
}

/* --- Ecriture ------------------------------------------------------ */

/**
 * Les pseudos ecrits a la main, retrouves avant publication.
 *
 * Une mention n'est un lien que si le membre est range a cote du post, et
 * cette liste ne se remplissait qu'en touchant une suggestion. Ecrire
 * « @Sam.78 » de bout en bout donnait un pseudo mort : la bonne
 * orthographe, aucun lien, et Sam jamais prevenu. Rien a l'ecran ne
 * distinguait les deux avant de publier.
 *
 * On complete donc la liste au moment ou le texte se fige. L'index des
 * pseudos donne l'identifiant en une lecture, et l'on relit le pseudo du
 * compte pour ranger l'orthographe du proprietaire plutot que celle de
 * qui l'a tape.
 *
 * Ce que cela n'ouvre pas : un « @ » colle a un mot reste une adresse,
 * et un pseudo mal orthographie reste du texte. On ne devine pas.
 */
export async function resoudreLesMentions(
  content: string,
  deja: PostMention[],
): Promise<PostMention[]> {
  const connus = new Set(deja.map((mention) => normalizeUsername(mention.username)));
  const aChercher = nomsPossibles(content)
    .map((formes) => formes.find((forme) => !connus.has(normalizeUsername(forme))) ?? null)
    .filter((forme): forme is string => forme !== null);
  if (aChercher.length === 0) return deja;

  const trouves = await Promise.all(
    [...new Set(aChercher)].map(async (nom) => {
      const uid = await readOptionalValue<string>(paths.username(nom)).catch(() => null);
      if (!uid) return null;
      const username = await readOptionalValue<string>(paths.userField(uid, 'username')).catch(
        () => null,
      );
      return username ? { id: uid, username } : null;
    }),
  );

  const ajouts = trouves.filter(
    (mention): mention is PostMention =>
      mention !== null && !deja.some((autre) => autre.id === mention.id),
  );
  return ajouts.length ? [...deja, ...ajouts] : deja;
}

/**
 * Qui a aime ce post.
 *
 * Reserve a son auteur, et volontairement a l'ecart du fil : un like ne
 * sonne pas, ne s'affiche a personne, et ne se compte qu'en nombre. Il
 * se regarde quand on vient le chercher, une fois, dans ses propres
 * posts — pas a chaque passage sur le fil, et pas par les autres.
 *
 * La retenue n'est pas technique : n'importe quel membre peut lire les
 * likes d'un post, c'est ainsi qu'ils se comptent. C'est l'application
 * qui choisit de ne les montrer qu'a celui qui a publie.
 */
export async function quiAAime(postId: string): Promise<PublicUser[]> {
  const ids = await readOptionalKeys(paths.postLikes(postId));
  if (ids.length === 0) return [];
  const users = await loadUsers(ids);
  return ids
    .map((id) => users.get(id) ?? unknownUser(id))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

export async function createPost(
  serverId: string,
  content: string,
  imageUrl: string | null = null,
  location: string | null = null,
  attachedEventId: string | null = null,
  mentions: PostMention[] = [],
  /** Le fil d'une association : la fonction verifie qu'on en est membre. */
  asso: string | null = null,
): Promise<Post> {
  const uid = requireUid();
  /*
    Le vide et la longueur sont verifies ici aussi, avant le reseau : la
    reponse est immediate au lieu d'attendre un aller-retour. Le serveur
    les revoit de toute facon — c'est lui qui fait foi.
  */
  const text = validateContent(content, Boolean(imageUrl) || Boolean(attachedEventId));
  const place = location?.trim() ? location.trim().slice(0, 80) : null;

  /*
    La photo part vers Storage ici, et pas plus tot : l'ecran de redaction
    n'a manipule qu'une data URL, ce qui lui donne son apercu immediat et
    laisse un brouillon abandonne sans rien laisser derriere lui.
  */
  const photo = imageUrl ? await uploadIfDataUrl(imageUrl) : null;

  /*
    Les pseudos ecrits a la main rejoignent ceux qu'on a touches.

    Sans cela, « @Sam.78 » tape de bout en bout restait du texte : bonne
    orthographe, aucun lien, et Sam jamais prevenu — rien a l'ecran ne
    distinguait les deux avant de publier. Voir `resoudreLesMentions`.
  */
  const cites = keepCited(text, await resoudreLesMentions(text, mentions));

  /*
    Le post n'est plus ecrit ici. Il part vers une fonction serveur qui
    examine son texte avant de le faire exister — les regles de la base
    refusent desormais au client de creer un post lui-meme. Un texte
    refuse n'est jamais enregistre : il n'y a rien a masquer ensuite.
  */
  const { id: postId, createdAt } = await appeler<
    {
      serverId: string;
      content: string;
      imageUrl: string | null;
      location: string | null;
      attachedEventId: string | null;
      mentions: PostMention[];
      asso: string | null;
    },
    { id: string; createdAt: number }
  >('publishPost', {
    serverId,
    content: text,
    imageUrl: photo,
    location: place,
    attachedEventId,
    // Seuls les membres encore cites dans le texte final sont gardes : on
    // peut avoir choisi quelqu'un dans la liste puis effacer son pseudo.
    mentions: cites,
    asso,
  });

  const author = await loadUser(uid);

  return {
    id: postId,
    serverId,
    author,
    content: text,
    createdAt: toIso(createdAt),
    likeCount: 0,
    commentCount: 0,
    bookmarkCount: 0,
    liked: false,
    bookmarked: false,
    attachedEventId,
    imageUrl: photo,
    location: place,
    mentions: cites,
    asso,
  };
}

/**
 * Suppression definitive, reservee a l'auteur.
 * Une seule mise a jour groupee emporte le post et ce qui n'a plus de
 * sens sans lui ; les signets des autres membres, eux, ne nous sont pas
 * accessibles et seront ignores a la lecture.
 */
export async function deletePost(post: Pick<Post, 'id' | 'serverId' | 'author'>): Promise<void> {
  const uid = requireUid();
  if (post.author.id !== uid) {
    throw new SiroccoDataError(t.fil.erreurs.supprimerLesSiens, 'forbidden');
  }

  // Les regles reconnaissent l'auteur d'un post via son index. Sans lui
  // — un post ecrit avant l'introduction de l'index — elles refuseraient
  // l'effacement groupe des likes et des commentaires, et c'est toute la
  // suppression qui echouerait. On se limite alors a ce qui est permis :
  // le post disparait, quelques enregistrements restent orphelins.
  const indexed = await exists(paths.postIndex(post.id));

  await updateValues({
    [paths.post(post.serverId, post.id)]: null,
    [paths.bookmarkedPost(uid, post.id)]: null,
    ...(indexed
      ? {
          [paths.comments(post.id)]: null,
          [paths.postLikes(post.id)]: null,
          [paths.postIndex(post.id)]: null,
        }
      : {}),
  });
}

/**
 * Like ou retrait du like.
 * L'etat stocke fait foi, pas celui de l'ecran : deux onglets ouverts ne
 * doivent pas compter deux fois. Le compteur passe par une transaction.
 */
export async function toggleLike(post: Post): Promise<Post> {
  const uid = requireUid();
  const likePath = paths.postLike(post.id, uid);
  const liked = await exists(likePath);

  /*
    Le like d'abord, le compteur ensuite — et seulement si le like passe.

    Les deux partaient de front. Or les regles refusent le like de qui a
    ete bloque par l'auteur du post : le compteur, lui, montait quand
    meme, et le post affichait un like que personne n'avait le droit de
    poser.
  */
  if (liked) await removeValue(likePath);
  else await writeValue(likePath, true);
  const likeCount = await bumpCounter(
    counterPath(post.serverId, post.id, 'likeCount'),
    liked ? -1 : 1,
  );

  return { ...post, liked: !liked, likeCount: Math.max(0, likeCount) };
}

/**
 * Signet d'un post, et le compteur qui va avec.
 *
 * Le signet lui-meme reste prive : il vit sous `bookmarkedPosts/{uid}`,
 * que son proprietaire seul peut lire. Ce qui devient public, c'est le
 * nombre — pose sur le post, comme celui des likes, parce qu'un fil
 * affiche cinquante posts et qu'aller compter branche par branche
 * couterait cinquante lectures pour cinquante chiffres.
 */
export async function toggleBookmark(post: Post): Promise<Post> {
  const uid = requireUid();
  const markPath = paths.bookmarkedPost(uid, post.id);
  const bookmarked = await exists(markPath);

  const [, bookmarkCount] = await Promise.all([
    bookmarked
      ? removeValue(markPath)
      : writeValue(markPath, {
          serverId: post.serverId,
          createdAt: Date.now(),
        } satisfies BookmarkRecord),
    bumpCounter(counterPath(post.serverId, post.id, 'bookmarkCount'), bookmarked ? -1 : 1),
  ]);

  return { ...post, bookmarked: !bookmarked, bookmarkCount: Math.max(0, bookmarkCount) };
}

/** Masquer : le post quitte mon feed, il reste chez les autres. */
export async function hidePost(postId: string): Promise<void> {
  await writeValue(paths.hiddenPost(requireUid(), postId), true);
}

/** Le post revient dans mon fil, a sa place. */
export async function unhidePost(postId: string): Promise<void> {
  await writeValue(paths.hiddenPost(requireUid(), postId), null);
}

/**
 * Les posts que j'ai masques, pour pouvoir revenir dessus.
 *
 * Masquer etait sans retour : le post quittait le fil et plus rien ne
 * permettait de le retrouver. Un post supprime depuis par son auteur
 * laisse une ligne orpheline, qu'on saute plutot que d'afficher un trou.
 */
export async function listHiddenPosts(): Promise<Post[]> {
  const uid = requireUid();
  const [rows, bookmarks] = await Promise.all([lireLesMasques(uid), readBookmarks(uid)]);
  if (rows.length === 0) return [];
  return hydrate(uid, sortByRecent(rows), new Set(keysOf(bookmarks)));
}

/**
 * Combien de posts la liste des masques montrera.
 *
 * Pas le nombre de marques : un post efface par son auteur laisse la
 * sienne, et le chiffre annoncerait un post qu'on ne trouve pas en ouvrant.
 */
export async function countHiddenPosts(): Promise<number> {
  return (await lireLesMasques(requireUid())).length;
}

/** Les posts masques encore lisibles, sans auteurs ni likes. */
async function lireLesMasques(uid: string): Promise<PostRow[]> {
  const ids = await readKeys(paths.hiddenPosts(uid));
  if (ids.length === 0) return [];

  const lus = await Promise.all(
    ids.map(async (postId): Promise<PostRow | null> => {
      const index = await readOptionalValue<{ serverId?: string }>(paths.postIndex(postId));
      if (!index?.serverId) return null;
      /* Un serveur ou l'on n'entre plus refuse la lecture : le post saute. */
      const record = await readValue<PostRecord>(paths.post(index.serverId, postId)).catch(
        () => null,
      );
      return record ? { ...record, id: postId, serverId: index.serverId } : null;
    }),
  );
  return lus.filter((row): row is PostRow => row !== null);
}

/**
 * Signalement, archive pour la moderation.
 *
 * Signaler vaut masquer : celui qui a leve la main ne doit plus croiser
 * le post en attendant qu'un humain tranche. Les deux ecritures partent
 * ensemble, sinon un signalement enregistre pourrait laisser le post en
 * place dans le feed de son auteur du signalement.
 */
export async function reportPost(
  post: Pick<Post, 'id' | 'serverId'>,
  reason: ReportReason,
  details = '',
): Promise<string> {
  const reporterId = requireUid();
  const reportId = newKey(paths.reports());

  await updateValues({
    [`${paths.reports()}/${reportId}`]: {
      type: 'post',
      postId: post.id,
      serverId: post.serverId,
      reporterId,
      reason,
      details: details.trim(),
      createdAt: Date.now(),
    },
    [paths.hiddenPost(reporterId, post.id)]: true,
  });
  return reportId;
}

/**
 * Efface une reponse, pour tout le monde.
 *
 * Reservee a son auteur, cote application comme cote regles. Les likes et
 * les signets qu'elle a recus restent en base : personne d'autre que leur
 * proprietaire ne peut les effacer, et une reponse disparue les rend
 * inoffensifs — la lecture part du commentaire, pas de ses likes.
 */
export async function deleteComment(comment: Comment): Promise<void> {
  const uid = requireUid();
  if (comment.author.id !== uid) {
    throw new SiroccoDataError(t.fil.erreurs.pasTaReponse, 'forbidden');
  }

  /*
    Ce que la suppression change pour le compteur du post.

    Il ne compte que les reponses posees sous le post lui-meme. Or on
    diminuait a chaque suppression, quelle que soit la reponse : effacer
    une reponse a une reponse — qui n'avait jamais ete comptee — faisait
    descendre le compte pour rien. Quatre suppressions d'essai, et un
    post a quatre reponses affichait zero.

    Et une suppression peut faire monter le compte. Les reponses de celle
    qu'on efface ne disparaissent pas avec elle : leur parent n'existant
    plus, elles remontent sous le post et deviennent des reponses
    directes. Il faut donc les ajouter.

    Le fil est lu une fois pour savoir combien elles sont. Une lecture sur
    un geste rare vaut mieux qu'un compteur qui derive a chaque fois.
  */
  const fil = await readList<CommentRecord>(paths.comments(comment.postId));
  /*
    Comptait-elle ? Elle comptait si elle etait posee sous le post, mais
    aussi si son propre parent avait deja disparu — elle etait alors
    remontee sous lui. Ne regarder que `parentId` laissait le compteur
    trop haut quand on effacait une orpheline.
  */
  const comptait =
    comment.parentId === null || !fil.some((autre) => autre.id === comment.parentId);
  const orphelines = fil.filter((autre) => autre.parentId === comment.id).length;
  const delta = (comptait ? -1 : 0) + orphelines;

  await removeValue(`${paths.comments(comment.postId)}/${comment.id}`);

  // Post efface entre-temps : il n'y a plus de compteur a corriger.
  if (delta !== 0) {
    const serverId = await findServerOf(comment.postId);
    if (serverId) await bumpCounter(counterPath(serverId, comment.postId, 'commentCount'), delta);
  }
}

/**
 * Signale une reponse.
 *
 * Meme branche que les signalements de posts, avec son propre `type` : la
 * moderation les lit au meme endroit, et distinguer les deux par un champ
 * evite d'avoir a tenir deux listes qui disent la meme chose.
 */
export async function reportComment(
  comment: Pick<Comment, 'id' | 'postId'>,
  reason: ReportReason,
  details = '',
): Promise<string> {
  const reporterId = requireUid();
  const reportId = newKey(paths.reports());

  await writeValue(`${paths.reports()}/${reportId}`, {
    type: 'comment',
    commentId: comment.id,
    postId: comment.postId,
    reporterId,
    reason,
    details: details.trim(),
    createdAt: Date.now(),
  });
  return reportId;
}

export async function addComment(
  postId: string,
  content: string,
  /* La reponse a laquelle on repond. `null` pose la reponse sous le post. */
  parentId: string | null = null,
  mentions: PostMention[] = [],
): Promise<Comment> {
  const uid = requireUid();
  const text = content.trim();
  if (!text) throw new SiroccoDataError(t.fil.erreurs.commentaireVide, 'invalid-content', 'content');

  /*
    Comme un post : la reponse part vers le serveur, qui l'examine avant
    de l'ecrire. Le compteur du post est incremente la-bas aussi — le
    faire ici laisserait un ecart des qu'un texte serait refuse.

    C'est aussi lui qui verifie que le parent existe et appartient bien a
    ce post : un client pourrait sinon rattacher sa reponse a n'importe
    quel commentaire de la base, et la faire paraitre sous un fil ou elle
    n'a rien a faire.
  */
  /*
    Les pseudos ecrits a la main rejoignent ceux qu'on a touches.

    Sans cela, « @Sam.78 » tape de bout en bout restait du texte : bonne
    orthographe, aucun lien, et Sam jamais prevenu — rien a l'ecran ne
    distinguait les deux avant de publier. Voir `resoudreLesMentions`.
  */
  const cites = keepCited(text, await resoudreLesMentions(text, mentions));

  const [{ id: commentId, createdAt }, author] = await Promise.all([
    appeler<
      { postId: string; content: string; parentId?: string; mentions?: PostMention[] },
      { id: string; createdAt: number }
    >('publishComment', {
      postId,
      content: text,
      ...(parentId ? { parentId } : {}),
      // Comme sur un post : seuls ceux encore cites dans le texte final.
      mentions: cites,
    }),
    loadUser(uid),
  ]);

  return {
    id: commentId,
    postId,
    author,
    parentId,
    content: text,
    mentions: cites,
    replyCount: 0,
    createdAt: toIso(createdAt),
    likeCount: 0,
    bookmarkCount: 0,
    liked: false,
    bookmarked: false,
  };
}

/* --- Coulisses ----------------------------------------------------- */

/** Casse et accents mis de cote : « medina » doit trouver « Médina ». */
function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** La recherche porte sur ce qu'on lit d'un post : son texte et son lieu. */
function matches(record: PostRecord, needle: string): boolean {
  if (!needle) return true;
  return fold(`${record.content} ${record.location ?? ''}`).includes(needle);
}

/**
 * Complete des posts bruts avec ce qui depend du lecteur.
 * Les auteurs partent en une seule passe, et le like se lit a la cle
 * exacte plutot qu'en rapatriant toute la liste des likes de chaque post.
 */
async function hydrate(uid: string, rows: PostRow[], bookmarked: Set<string>): Promise<Post[]> {
  if (rows.length === 0) return [];

  const [authors, likes] = await Promise.all([
    loadUsers(rows.map((row) => row.authorId)),
    Promise.all(rows.map((row) => exists(paths.postLike(row.id, uid)))),
  ]);

  return rows.map((row, index) => ({
    id: row.id,
    serverId: row.serverId,
    // loadUsers garantit une entree par identifiant demande.
    author: authors.get(row.authorId)!,
    content: row.content,
    createdAt: toIso(row.createdAt),
    likeCount: row.likeCount ?? 0,
    commentCount: row.commentCount ?? 0,
    bookmarkCount: row.bookmarkCount ?? 0,
    liked: likes[index],
    bookmarked: bookmarked.has(row.id),
    attachedEventId: row.attachedEventId ?? null,
    imageUrl: row.imageUrl ?? null,
    mentions: row.mentions ?? [],
    location: row.location ?? null,
    asso: row.asso ?? null,
  }));
}

/** Mes signets, avec leur serveur : une seule lecture sert a tout l'ecran. */
async function readBookmarks(uid: string): Promise<Record<string, BookmarkRecord>> {
  return (await readValue<Record<string, BookmarkRecord>>(paths.bookmarkedPosts(uid))) ?? {};
}

/** Le feed se lit du plus recent au plus ancien. */
function sortByRecent(rows: PostRow[]): PostRow[] {
  return rows.sort((left, right) => right.createdAt - left.createdAt);
}

/**
 * Retrouve le serveur d'un post.
 * Les commentaires sont ranges par postId seul, alors que le compteur
 * vit dans le post : faute d'index `postServers/{postId}`, on demande a
 * chaque serveur s'il l'heberge, en parallele et pour une lecture minime.
 */
async function findServerOf(postId: string): Promise<string | null> {
  const indexed = await readValue<{ serverId: string }>(paths.postIndex(postId));
  if (indexed?.serverId) return indexed.serverId;

  // Repli pour les posts anterieurs a l'index.
  const serverIds = await readKeys(paths.servers());
  const matches = await Promise.all(
    serverIds.map(async (serverId) =>
      /* Un serveur ferme repond « non » plutot que de lever. */
      (await exists(paths.post(serverId, postId)).catch(() => false)) ? serverId : null,
    ),
  );
  return matches.find((serverId): serverId is string => serverId !== null) ?? null;
}

/** Le compteur des signets d'une reponse, champ de la reponse elle-meme. */
function compteurDeReponse(postId: string, commentId: string): string {
  return `${paths.comment(postId, commentId)}/bookmarkCount`;
}

/** `paths` s'arrete au post ; les compteurs sont des champs de celui-ci. */
function counterPath(
  serverId: string,
  postId: string,
  field: 'likeCount' | 'commentCount' | 'bookmarkCount',
): string {
  return `${paths.post(serverId, postId)}/${field}`;
}

function validateContent(content: string, hasAttachment = false): string {
  const text = content.trim();
  if (!text && !hasAttachment) {
    throw new SiroccoDataError(
      t.fil.erreurs.vide,
      'invalid-content',
      'content',
    );
  }
  if (text.length > POST_MAX_LENGTH) {
    throw new SiroccoDataError(
      t.fil.erreurs.tropLong(POST_MAX_LENGTH),
      'invalid-content',
      'content',
    );
  }
  return text;
}
